import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Toggle } from '@/components/ui/toggle';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Trash2, Paintbrush, Move, Shrink, Expand, Volume2, Volume1, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import TextStyleEditor from './TextStyleEditor';
import { applySpeedChange } from '@/lib/layers/applySpeedChange';
import { applyLayerFitMode } from '@/lib/layers/applyLayerFitMode';
import { clampLayerFrame } from '@/lib/layers/clampLayerFrame';
import { isClipMirror } from '@/lib/layers/types';
import type { MediaLayer } from '@/lib/layers/types';

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

/** Every control sits on one 32px baseline, under a micro-cap label. Fields are
 *  bottom-aligned by the row, so the controls line up even though the labels
 *  differ in width. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
      {label}
    </span>
    <div className="flex h-8 items-center gap-2">{children}</div>
  </div>
);

/** Hairline rule between functional groups. Hidden on the wrapped (narrow)
 *  layout, where a rule at the start of a row reads as noise. */
const Divider: React.FC = () => (
  <div aria-hidden className="hidden h-8 w-px self-end bg-border/70 sm:block" />
);

/** A slider with its value spelled out. The old bar showed none, so "how loud
 *  is this layer" could only be answered by eyeballing the thumb. */
const ValueSlider: React.FC<{
  testId: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}> = ({ testId, value, min, max, step, onChange, format }) => (
  <>
    <Slider
      data-testid={testId}
      className="w-24"
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
    />
    <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
      {format(value)}
    </span>
  </>
);

const segmentButton =
  'flex h-full items-center gap-1.5 px-2.5 text-xs font-medium text-muted-foreground ' +
  'transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset';

const percent = (v: number) => `${Math.round(v)}%`;

interface Props {
  layer: MediaLayer;
  clipDuration: number;
  onPatch: (id: string, patch: Partial<MediaLayer>) => void;
  onDelete: (id: string) => void;
  reframeActive: boolean;
  onReframeToggle: () => void;
}

/** Contextual control bar for the selected layer: framing (image/video), speed
 *  (video/audio), volume, opacity, text content + Style popover, delete.
 *  Rendered by ProjectEditor in its own row BELOW the preview — never over it,
 *  so it cannot obstruct the frame being edited — and outside the video frame,
 *  which a portrait canvas is far too narrow to hold.
 *
 *  It deliberately shows no filename: the timeline lane already labels the
 *  layer, and a truncated `snaptik_76591821…` bought nothing but width. */
export const LayerExpansionRow: React.FC<Props> = ({
  layer, clipDuration, onPatch, onDelete, reframeActive, onReframeToggle,
}) => {
  const { t } = useTranslation();
  const isMedia = layer.type === 'video' || layer.type === 'audio';
  const isMirror = isClipMirror(layer);
  const isVideo = layer.type === 'video';
  // Fit/fill re-lay the rect over the whole frame. Only image and video have a
  // media aspect to preserve; text has no intrinsic size and audio no picture.
  const canFitToFrame = layer.type === 'image' || isVideo;

  const volumePct = isMedia ? Math.round(layer.volume * 100) : 0;
  const VolumeIcon = volumePct === 0 ? VolumeX : volumePct < 50 ? Volume1 : Volume2;

  return (
    <div
      data-testid="layer-expansion"
      // Part of the layer UI: clicking the bar must not count as an outside
      // click and deselect the very layer the bar is editing.
      data-layer-ui
      className={cn(
        'flex flex-wrap items-end gap-x-3 gap-y-3 rounded-xl px-3 py-2.5',
        // A quiet surface, not the floating glass panel this used to be when it
        // overlaid the preview. The primary-tinted edge marks it as belonging
        // to the selected layer.
        'border border-primary/25 bg-card shadow-sm',
      )}
    >
      {canFitToFrame && (
        <>
          <Field label={t('projectEditor.layers.framing')}>
            {/* Segmented pair: two ways to say the same thing about the frame. */}
            <div className="flex h-8 items-center rounded-md border border-input">
              <button
                type="button"
                data-testid="layer-fit"
                title={t('projectEditor.layers.fit')}
                aria-label={t('projectEditor.layers.fit')}
                className={cn(segmentButton, 'rounded-l-[5px]')}
                onClick={() => onPatch(layer.id, { rect: applyLayerFitMode({ rect: layer.rect, mode: 'fit' }) })}
              >
                <Shrink className="h-3.5 w-3.5" />
                {t('projectEditor.layers.fit')}
              </button>
              <div aria-hidden className="h-4 w-px bg-border" />
              <button
                type="button"
                data-testid="layer-fill"
                title={t('projectEditor.layers.fill')}
                aria-label={t('projectEditor.layers.fill')}
                className={cn(segmentButton, 'rounded-r-[5px]')}
                onClick={() => onPatch(layer.id, { rect: applyLayerFitMode({ rect: layer.rect, mode: 'fill' }) })}
              >
                <Expand className="h-3.5 w-3.5" />
                {t('projectEditor.layers.fill')}
              </button>
            </div>
          </Field>
          <Divider />
        </>
      )}

      {isMedia && !isMirror && (
        <>
          <Field label={t('projectEditor.layers.speed')}>
            <Select
              value={String(layer.speed)}
              onValueChange={(v) =>
                onPatch(layer.id, applySpeedChange({
                  start: layer.start, trimStart: layer.trimStart, trimEnd: layer.trimEnd,
                  speed: Number(v), clipDuration,
                }))}
            >
              <SelectTrigger data-testid="layer-speed" className="h-8 w-[4.75rem] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPEED_OPTIONS.map((s) => (
                  <SelectItem key={s} value={String(s)} className="text-xs">{s}×</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Divider />
        </>
      )}

      {isVideo && (
        <>
          {/* Toggle and zoom are one control group — reframing IS panning and
              zooming — so they share a label instead of competing for two. */}
          <Field label={t('projectEditor.layers.reframe')}>
            <Toggle
              data-testid="layer-reframe"
              pressed={reframeActive}
              onPressedChange={onReframeToggle}
              size="sm"
              variant="outline"
              className="h-8 w-8 shrink-0 p-0"
              title={t('projectEditor.layers.reframe')}
              aria-label={t('projectEditor.layers.reframe')}
            >
              <Move className="h-3.5 w-3.5" />
            </Toggle>
            {(reframeActive || layer.frame) && (
              <ValueSlider
                testId="layer-zoom"
                value={layer.frame?.scale ?? 1}
                min={1}
                max={3}
                step={0.1}
                format={(v) => `${v.toFixed(1)}×`}
                onChange={(v) =>
                  onPatch(layer.id, {
                    frame: clampLayerFrame({ ...(layer.frame ?? { scale: 1, offsetX: 0, offsetY: 0 }), scale: v }),
                  })}
              />
            )}
          </Field>
          <Divider />
        </>
      )}

      {isMedia && (
        <Field label={t('projectEditor.layers.volume')}>
          <VolumeIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <ValueSlider
            testId="layer-volume"
            value={volumePct}
            min={0}
            max={100}
            step={1}
            format={percent}
            onChange={(v) => onPatch(layer.id, { volume: v / 100 })}
          />
        </Field>
      )}

      {layer.type !== 'audio' && (
        <Field label={t('projectEditor.layers.opacity')}>
          <ValueSlider
            testId="layer-opacity"
            value={Math.round(layer.opacity * 100)}
            min={0}
            max={100}
            step={1}
            format={percent}
            onChange={(v) => onPatch(layer.id, { opacity: v / 100 })}
          />
        </Field>
      )}

      {layer.type === 'text' && (
        <>
          <Divider />
          <Field label={t('projectEditor.layers.textContent')}>
            <input
              data-testid="text-layer-content"
              value={layer.text}
              onChange={(e) => onPatch(layer.id, { text: e.target.value, name: e.target.value || layer.name })}
              className="h-8 w-40 rounded-md border border-input bg-transparent px-2 text-xs
                         placeholder:text-muted-foreground focus-visible:outline-none
                         focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="layer-style-button" size="sm" variant="secondary" className="h-8">
                  <Paintbrush className="mr-1 h-3.5 w-3.5" />
                  {t('projectEditor.layers.style')}
                </Button>
              </PopoverTrigger>
              {/* A FIXED height, not max-height. Radix anchors a `side="top"`
                  popover once, from the content height at open time. Expanding a
                  section afterwards grew the box downward and it never
                  re-anchored, so it hung off the bottom of the screen — and
                  overflow-y-auto never engaged, because max-height was never
                  reached. Pinning the height means the box never resizes, so the
                  initial anchor stays correct and the content scrolls inside it.
                  Inline style (not a Tailwind arbitrary value) so min() is
                  emitted verbatim. */}
              <PopoverContent
                align="start"
                side="top"
                collisionPadding={12}
                style={{
                  height:
                    'min(28rem, var(--radix-popover-content-available-height, 28rem))',
                }}
                className="w-[22rem] overflow-y-auto"
              >
                <TextStyleEditor layer={layer} onPatch={(patch) => onPatch(layer.id, patch)} />
              </PopoverContent>
            </Popover>
          </Field>
        </>
      )}

      <Divider />

      <Button
        data-testid="layer-delete"
        size="icon"
        variant="ghost"
        // ml-auto only bites once the bar stretches (the wrapped layout); at
        // content width the divider above is what keeps it off the last slider.
        className="ml-auto h-8 w-8 self-end text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        title={t('projectEditor.layers.deleteLayer')}
        aria-label={t('projectEditor.layers.deleteLayer')}
        onClick={() => onDelete(layer.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default LayerExpansionRow;
